/*!
 * @copyright
 * Copyright(c) 2018 SICK AG
 */
import { Mesh } from 'three';
import { Director, Stage, Materials, Solids, Pointer, CapNames,
   DefaultCaps, DefaultNavigators, Events, ObjectClickedEvent,
ObjectDraggedEvent, ObjectReleasedEvent, OverlayObject } from '@sick-davinci/glow';

import { Checkbox } from '@sick-davinci/basic-elements';

export const dependencies = [Checkbox];  // avoid wepack removal

/**
 * Demo:    DragAndDrop
 * Description:
 *   This demo shows how elements on the scene can be dragged with a mouse.
 * Features:
 *  - Orbit navigation with custom settings
 *  - Capability handling
 *  - Native threejs code
 *  - Pointer (Raycasting)
 *  - Object interaction
 *  - Actors
 *  - HTML Overlay
 */
export const run = ((viewParent: HTMLElement) => {

  /*ObjectSelector.showDebugUI = {
    showStates: true,
    showConsole: true,
  };*/

  // * Setup the stage *
  const stage = new Stage(viewParent, {
    cameras: [
      {
        name: 'default',
        from: [10, 10, 10],
        to: [0, 0, 0],
        up: [0, 1, 0],
        fov: 60,
      },
    ],
    scenes: [{
      name: 'default',
      }],
    viewports: [
      {
        name: 'viewPort',
        scene: 'default',
        camera: 'default',
        navigator: 'orbit',
        bgcolor: 0x393939,
      },
    ],
  });

  // * Add an Orbit Navigator
  DefaultNavigators.initOrbit(stage, 'orbit');

  // * Data handling using capabilities
  const director = new Director(stage);
  DefaultCaps.init(director);

  director.handle(CapNames.AXIS, { axisLength: 30 });
  director.handle(CapNames.LIGHT, { payload: { lightType: 'spot', color: 0xff7777 }, orientation: { position: [100, -100, -100] } });
  director.handle(CapNames.LIGHT, { payload: { lightType: 'spot', color: 0x4477ff }, orientation: { position: [100, 100, 100] } });
  director.handle(CapNames.LIGHT, { payload: { lightType: 'ambient', color: 0xffffff } });

  stage.start();

  const label = document.createElement( 'div' );
  label.className = 'label';
  label.innerText = 'This is an overlay';
  label.style.marginTop = '-1em';
  label.style.position = 'fixed';
  label.style.top = '5vh';
  label.style.left = '30px';
  label.style.width = '120px';
  label.style.backgroundColor = '#fff';
  label.style.borderRadius = '5px';
  label.style.padding = '10px';
  label.style.boxShadow = '10px 10px 10px rgba(0, 0, 0, 0.25)';
  label.style.zIndex = '1000';

  const objLabel = new OverlayObject( label );
  objLabel.translateX(4);

  // * Add a box using native threejs code
  const box = new Mesh(Solids.createBox(4, 4, 4), Materials.create({ type: 'standard', params: { color: 0x00ffff } }));
  box.add(objLabel);
  stage.applyThreeDirect((scene) => {
    scene.add(box);
  });

  // * must be called in order to get drag events. If false navigation is always possible but no elment interaction
  stage.enableDragAndDrop(true);

  // * if object is released the box is moved back to original position
  const animationToOrigin = {
    capability: CapNames.ACTOR,
    payload: {
      objectId: box.id,
      act: 'move',
      action:
      {
        name: 'anyname',
        duration: 0.4,
        target:
          [
            { name: 'position', value: [0, 0, 0], type: 'vector' },
          ],
      },
    },
  };

  // * Raycasting: get the 3d cooridinates from 2d mouse-position
  const pointer = new Pointer(stage.getCamera('default'));

  viewParent.addEventListener(Events.OBJECTCLICKED, ({detail}: CustomEvent<ObjectClickedEvent>) => {
    if (detail.new) {
      pointer.changePoint(detail.new.object.position);
    }
  });

  // * Let the box follow the mouse-pointer
  viewParent.addEventListener(Events.OBJECTDRAGGED, ({detail}: CustomEvent<ObjectDraggedEvent>) => {
    if (detail.object) {
      const pos = detail.relPosition;
      const p = pointer.to3DSpace(pos.mousex, pos.mousey);
      box.position.x = p.x;
      box.position.y = p.y;
      box.position.z = p.z;
    }
  });

  // * Trigger the box going back to origin
  viewParent.addEventListener(Events.OBJECTRELEASED, ({detail}: CustomEvent<ObjectReleasedEvent>) => {
    if (detail.object) {
      director.handleCapData(animationToOrigin);
    }
  });

  const checkBox = document.getElementById('enablednd') as Checkbox;
  checkBox.addEventListener('change', ((evt) => stage.enableDragAndDrop(checkBox.checked) ));

});
